import { useState } from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { InfoCard, Table, WarningPanel } from '@backstage/core-components';

type Field = { name: string; number: string; label: string; fullType: string; description: string };
type Message = { fullName: string; description: string; fields: Field[] };
type Enum = { fullName: string; description: string; values: { name: string; number: string; description: string }[] };
type Method = { name: string; requestFullType: string; responseFullType: string; requestStreaming: boolean; responseStreaming: boolean; description: string };
type ProtoFile = {
  name: string; package: string; description: string;
  services: { fullName: string; description: string; methods: Method[] }[];
  messages: Message[]; enums: Enum[];
};

function ProtoDefinition({ definition }: { definition: string }) {
  const [query, setQuery] = useState('');
  let files: ProtoFile[];
  try {
    files = JSON.parse(definition).files;
    if (!Array.isArray(files)) throw new Error('Missing files');
  } catch {
    return <WarningPanel title="Invalid protobuf documentation">Rebuild the Backstage image to regenerate this API definition.</WarningPanel>;
  }
  const matches = (name: string, description: string) => `${name} ${description}`.toLowerCase().includes(query.toLowerCase());
  const options = { paging: false, search: false, padding: 'dense' as const };
  return <Box>
    <TextField label="Find a service, message, enum or field" fullWidth value={query} onChange={event => setQuery(event.target.value)} margin="normal" />
    {files.map(file => {
      const showAll = matches(file.name, file.package);
      const services = (file.services ?? []).filter(service => showAll || matches(service.fullName, service.description) || service.methods.some(method => matches(method.name, method.description)));
      const messages = (file.messages ?? []).filter(message => showAll || matches(message.fullName, message.description) || message.fields.some(field => matches(field.name, field.description)));
      const enums = (file.enums ?? []).filter(value => showAll || matches(value.fullName, value.description));
      if (!services.length && !messages.length && !enums.length) return null;
      return <Box key={file.name} mb={4}>
        <Typography variant="h5">{file.package}</Typography>
        <Typography color="textSecondary">{file.name}</Typography>
        <Typography style={{ whiteSpace: 'pre-wrap' }}>{file.description}</Typography>
        {services.map(service => <Box key={service.fullName} my={2}>
          <InfoCard title={service.fullName} subheader="gRPC service">
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{service.description}</Typography>
            <Table title="RPC methods" options={options} data={service.methods} columns={[
              { title: 'Method', field: 'name' },
              { title: 'Request', render: method => `${method.requestStreaming ? 'stream ' : ''}${method.requestFullType}` },
              { title: 'Response', render: method => `${method.responseStreaming ? 'stream ' : ''}${method.responseFullType}` },
              { title: 'Description', field: 'description' },
            ]} />
          </InfoCard>
        </Box>)}
        {messages.map(message => <Box key={message.fullName} my={2}>
          <InfoCard title={message.fullName} subheader="Message">
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{message.description}</Typography>
            <Table title="Fields" options={options} data={message.fields} columns={[
              { title: 'Tag', field: 'number' }, { title: 'Field', field: 'name' },
              { title: 'Type', field: 'fullType' }, { title: 'Label', field: 'label' },
              { title: 'Description', field: 'description' },
            ]} />
          </InfoCard>
        </Box>)}
        {enums.map(value => <Box key={value.fullName} my={2}>
          <InfoCard title={value.fullName} subheader="Enum">
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{value.description}</Typography>
            <Table title="Values" options={options} data={value.values} columns={[
              { title: 'Name', field: 'name' }, { title: 'Number', field: 'number' },
              { title: 'Description', field: 'description' },
            ]} />
          </InfoCard>
        </Box>)}
      </Box>;
    })}
  </Box>;
}

// Uses protoc-gen-doc's documented JSON schema. Keeping the viewer local
// avoids grpc-docs' obsolete dependency on an unavailable external Git host.
export const protobufWidget = {
  type: 'grpc-docs', title: 'Protocol Buffers', rawLanguage: 'json',
  component: (definition: string) => <ProtoDefinition definition={definition} />,
};
